import type { AdminConfig } from './auth';

export interface SessionMessage {
  role: 'user' | 'assistant' | 'status' | 'error';
  text: string;
  at: string;
  durationMs: number | null;
}

export interface SessionView {
  sessionId: string;
  label: string | null;
  branch: string;
  agentId: string | null;
  activeRunId: string | null;
  status: string;
  lastError: string | null;
  draftUpdatedAt: string | null;
  lastAssistantText: string | null;
  headSha: string | null;
  durableTag: string | null;
  publishedAt: string | null;
  publishedSha: string | null;
  publishBuildId: string | null;
  messages?: SessionMessage[];
  createdAt?: string;
  updatedAt?: string;
}

export interface SessionSummary {
  sessionId: string;
  label: string | null;
  branch: string;
  status: string;
  lastAssistantText: string | null;
  draftUpdatedAt: string | null;
  publishedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

async function readError(response: Response): Promise<string> {
  try {
    const body = (await response.json()) as { error?: string };
    if (typeof body.error === 'string' && body.error.length > 0) {
      return body.error;
    }
  } catch {
    // ignore
  }
  return `Request failed (${response.status}).`;
}

export async function apiFetch(
  config: AdminConfig,
  idToken: string,
  path: string,
  init?: RequestInit,
): Promise<Response> {
  const base = config.apiUrl.replace(/\/$/, '');
  const response = await fetch(`${base}${path}`, {
    ...init,
    headers: {
      Authorization: `Bearer ${idToken}`,
      'Content-Type': 'application/json',
      ...(init?.headers ?? {}),
    },
  });
  return response;
}

export async function whoami(
  config: AdminConfig,
  idToken: string,
): Promise<{ email: string; isSuperAdmin: boolean }> {
  const response = await apiFetch(config, idToken, '/whoami');
  if (!response.ok) {
    throw new Error(await readError(response));
  }
  return (await response.json()) as { email: string; isSuperAdmin: boolean };
}

export async function createSession(
  config: AdminConfig,
  idToken: string,
  label?: string | null,
): Promise<SessionView> {
  const response = await apiFetch(config, idToken, '/sessions', {
    method: 'POST',
    body: JSON.stringify({
      label:
        label !== undefined && label !== null && label.trim().length > 0
          ? label.trim()
          : null,
    }),
  });
  if (!response.ok) {
    throw new Error(await readError(response));
  }
  const body = (await response.json()) as { session: SessionView };
  return body.session;
}

export async function renameSession(
  config: AdminConfig,
  idToken: string,
  sessionId: string,
  label: string | null,
): Promise<SessionView> {
  const response = await apiFetch(
    config,
    idToken,
    `/sessions/${encodeURIComponent(sessionId)}`,
    {
      method: 'PATCH',
      body: JSON.stringify({ label }),
    },
  );
  if (!response.ok) {
    throw new Error(await readError(response));
  }
  const body = (await response.json()) as { session: SessionView };
  return body.session;
}

export async function deleteSession(
  config: AdminConfig,
  idToken: string,
  sessionId: string,
): Promise<void> {
  const response = await apiFetch(
    config,
    idToken,
    `/sessions/${encodeURIComponent(sessionId)}`,
    { method: 'DELETE' },
  );
  if (!response.ok) {
    throw new Error(await readError(response));
  }
}

export async function listSessions(
  config: AdminConfig,
  idToken: string,
): Promise<readonly SessionSummary[]> {
  const response = await apiFetch(config, idToken, '/sessions');
  if (!response.ok) {
    throw new Error(await readError(response));
  }
  const body = (await response.json()) as { sessions: SessionSummary[] };
  return body.sessions;
}

export async function sendMessage(
  config: AdminConfig,
  idToken: string,
  sessionId: string,
  message: string,
): Promise<{ session: SessionView; runId: string | null }> {
  const response = await apiFetch(
    config,
    idToken,
    `/sessions/${encodeURIComponent(sessionId)}/messages`,
    {
      method: 'POST',
      body: JSON.stringify({ message }),
    },
  );
  if (!response.ok) {
    throw new Error(await readError(response));
  }
  return (await response.json()) as {
    session: SessionView;
    runId: string | null;
  };
}

/**
 * Opens the streaming Function URL and yields parsed SSE events until `done`.
 * Falls back to the caller when streamUrl is missing or the stream errors.
 */
export async function* streamRun(
  config: AdminConfig,
  idToken: string,
  sessionId: string,
  runId: string,
): AsyncGenerator<{ event: string; data: unknown }, void, undefined> {
  const base = (config.streamUrl ?? '').replace(/\/$/, '');
  if (base.length === 0) {
    throw new Error('streamUrl is not configured.');
  }
  const url = `${base}/sessions/${encodeURIComponent(sessionId)}/runs/${encodeURIComponent(runId)}/stream`;
  const response = await fetch(url, {
    headers: {
      Authorization: `Bearer ${idToken}`,
      Accept: 'text/event-stream',
    },
  });
  if (!response.ok) {
    throw new Error(await readError(response));
  }
  if (response.body === null) {
    throw new Error('Stream response had no body.');
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  try {
    while (true) {
      const chunk = await reader.read();
      if (chunk.done) {
        break;
      }
      buffer += decoder.decode(chunk.value, { stream: true });
      const parts = buffer.split('\n\n');
      buffer = parts.pop() ?? '';
      for (const part of parts) {
        if (part.trim().length === 0) {
          continue;
        }
        let event = 'message';
        const dataLines: string[] = [];
        for (const line of part.split('\n')) {
          if (line.startsWith('event:')) {
            event = line.slice(6).trim();
          } else if (line.startsWith('data:')) {
            dataLines.push(line.slice(5).trimStart());
          }
        }
        const raw = dataLines.join('\n');
        let data: unknown = {};
        if (raw.length > 0) {
          try {
            data = JSON.parse(raw) as unknown;
          } catch {
            data = raw;
          }
        }
        yield { event, data };
        if (event === 'done') {
          return;
        }
      }
    }
  } finally {
    reader.releaseLock();
  }
}

export async function fetchSession(
  config: AdminConfig,
  idToken: string,
  sessionId: string,
): Promise<SessionView> {
  const response = await apiFetch(
    config,
    idToken,
    `/sessions/${encodeURIComponent(sessionId)}`,
  );
  if (!response.ok) {
    throw new Error(await readError(response));
  }
  const body = (await response.json()) as { session: SessionView };
  return body.session;
}

export async function fetchSessionOrNull(
  config: AdminConfig,
  idToken: string,
  sessionId: string,
): Promise<SessionView | null> {
  const response = await apiFetch(
    config,
    idToken,
    `/sessions/${encodeURIComponent(sessionId)}`,
  );
  if (response.status === 404) {
    return null;
  }
  if (!response.ok) {
    throw new Error(await readError(response));
  }
  const body = (await response.json()) as { session: SessionView };
  return body.session;
}

export async function publishSession(
  config: AdminConfig,
  idToken: string,
  sessionId: string,
): Promise<{
  session: SessionView;
  mergeSha: string | null;
  buildId: string | null;
  siteUrl: string;
}> {
  const response = await apiFetch(
    config,
    idToken,
    `/sessions/${encodeURIComponent(sessionId)}/publish`,
    {
      method: 'POST',
      body: '{}',
    },
  );
  if (!response.ok) {
    throw new Error(await readError(response));
  }
  return (await response.json()) as {
    session: SessionView;
    mergeSha: string | null;
    buildId: string | null;
    siteUrl: string;
  };
}

export interface PublishStatusView {
  session: SessionView;
  buildId: string | null;
  buildStatus: string | null;
  currentPhase: string | null;
  complete: boolean;
  siteUrl: string;
  errorMessage: string | null;
}

export async function fetchPublishStatus(
  config: AdminConfig,
  idToken: string,
  sessionId: string,
): Promise<PublishStatusView> {
  const response = await apiFetch(
    config,
    idToken,
    `/sessions/${encodeURIComponent(sessionId)}/publish`,
  );
  if (!response.ok) {
    throw new Error(await readError(response));
  }
  return (await response.json()) as PublishStatusView;
}

export async function fetchDraft(
  config: AdminConfig,
  idToken: string,
  sessionId: string,
): Promise<{
  home: HomeDraft | null;
  site: SiteDraft | null;
  theme: ThemeDraft | null;
  updatedAt: string | null;
}> {
  const response = await apiFetch(
    config,
    idToken,
    `/sessions/${encodeURIComponent(sessionId)}/draft`,
  );
  if (!response.ok) {
    throw new Error(await readError(response));
  }
  return (await response.json()) as {
    home: HomeDraft | null;
    site: SiteDraft | null;
    theme: ThemeDraft | null;
    updatedAt: string | null;
  };
}

export interface CtaDraft {
  label?: string;
  to?: string;
}

export interface HomeDraft {
  hero?: {
    eyebrow?: string;
    headline?: string;
    subhead?: string;
    posture?: string;
    primaryCta?: CtaDraft;
    secondaryCta?: CtaDraft;
  };
  apps?: {
    eyebrow?: string;
    headline?: string;
    items?: Array<{ name?: string; body?: string }>;
  };
  problem?: { eyebrow?: string; headline?: string; body?: string };
  howItWorks?: {
    eyebrow?: string;
    headline?: string;
    body?: string;
    steps?: Array<{ title?: string; body?: string }>;
  };
  roadmap?: { eyebrow?: string; headline?: string; body?: string };
  credibility?: { eyebrow?: string; headline?: string; body?: string };
  act?: {
    eyebrow?: string;
    headline?: string;
    body?: string;
    trust?: string;
    primaryCta?: CtaDraft;
  };
}

export interface SiteDraft {
  name?: string;
  nav?: Array<{ label: string; href: string; cta?: boolean }>;
  footer?: {
    tagline?: string;
    statusLine?: string;
    honesty?: string;
    legal?: Array<{ label: string; href: string }>;
  };
}

/** Brand tokens from `src/data/theme.json` (CSS custom property values). */
export type ThemeDraft = Record<string, string>;
